import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdAdd, MdContentCopy, MdEdit, MdLink } from "react-icons/md";
import Card from "components/card";
import {
  clienteService,
  Cliente,
  ClienteInput,
  TipoEvaluacion,
} from "services/clienteService";

const Clientes = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editando, setEditando] = useState<Cliente | "nuevo" | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);

    try {
      setClientes(await clienteService.listar());
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudieron cargar los clientes."
      );
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const copiarLink = (url: string) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setAviso("Link copiado al portapapeles.");
    setTimeout(() => setAviso(null), 3000);
  };

  return (
    <div className="mt-5 space-y-5">
      <Card extra="w-full p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-navy-700 dark:text-white">
              Clientes
            </h2>
            <p className="text-sm text-gray-500">
              Instituciones para las que hacemos evaluaciones.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setEditando("nuevo")}
            className="flex items-center gap-1 rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            <MdAdd /> Nuevo cliente
          </button>
        </div>

        {aviso && (
          <div className="mb-3 rounded-md bg-green-50 p-2 text-sm text-green-700">
            {aviso}
          </div>
        )}

        {error && (
          <div className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {cargando ? (
          <div className="py-10 text-center text-gray-500">Cargando…</div>
        ) : clientes.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            Todavía no hay clientes. Creá el primero para generar su link.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {clientes.map((c) => (
              <div
                key={c.id}
                className="rounded-lg border border-gray-200 p-4 dark:border-white/10"
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {c.logo_url && (
                      <img
                        src={c.logo_url}
                        alt={c.nombre}
                        className="h-10 w-auto"
                      />
                    )}
                    <div>
                      <p className="font-semibold text-navy-700 dark:text-white">
                        {c.nombre}
                      </p>
                      <p className="text-xs text-gray-500">/{c.slug}</p>
                    </div>
                  </div>

                  {!c.activo && (
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">
                      Inactivo
                    </span>
                  )}
                </div>

                <div className="mb-3 flex gap-4 text-sm">
                  <span className="text-gray-600 dark:text-gray-300">
                    <strong>{c.evaluaciones}</strong> evaluaciones
                  </span>
                  <span className="text-green-600">
                    <strong>{c.completadas}</strong> completadas
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/admin/evaluaciones?cliente=${c.id}`)
                    }
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50"
                  >
                    Ver evaluaciones
                  </button>
                  <button
                    type="button"
                    onClick={() => copiarLink(c.url_publica)}
                    className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50"
                  >
                    <MdContentCopy /> Copiar link
                  </button>
                  <a
                    href={c.url_publica}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50"
                  >
                    <MdLink /> Abrir
                  </a>
                  <button
                    type="button"
                    onClick={() => setEditando(c)}
                    className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50"
                  >
                    <MdEdit /> Editar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editando && (
        <FormularioCliente
          cliente={editando === "nuevo" ? null : editando}
          onCerrar={() => setEditando(null)}
          onGuardado={() => {
            setEditando(null);
            cargar();
          }}
        />
      )}
    </div>
  );
};

function FormularioCliente({
  cliente,
  onCerrar,
  onGuardado,
}: {
  cliente: Cliente | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const [form, setForm] = useState<ClienteInput>({
    nombre: cliente?.nombre ?? "",
    slug: cliente?.slug ?? "",
    campaign_id: cliente?.campaign_id ?? null,
    tipo_evaluacion: cliente?.tipo_evaluacion ?? "",
    logo_url: cliente?.logo_url ?? "",
    logo_secundario_url: cliente?.logo_secundario_url ?? "",
    titulo: cliente?.titulo ?? "",
    descripcion: cliente?.descripcion ?? "",
    consentimiento: cliente?.consentimiento ?? "",
    email_notificaciones: cliente?.email_notificaciones ?? "",
    activo: cliente?.activo ?? true,
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tipos, setTipos] = useState<TipoEvaluacion[]>([]);

  useEffect(() => {
    clienteService.tiposEvaluacion().then(setTipos).catch(() => setTipos([]));
  }, []);

  const cambiar = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);

    // Los vacíos se mandan como null para no guardar cadenas vacías.
    const payload: ClienteInput = {
      ...form,
      slug: form.slug || undefined,
      logo_url: form.logo_url || null,
      logo_secundario_url: form.logo_secundario_url || null,
      titulo: form.titulo || null,
      descripcion: form.descripcion || null,
      consentimiento: form.consentimiento || null,
      email_notificaciones: form.email_notificaciones || null,
    };

    try {
      if (cliente) {
        await clienteService.actualizar(cliente.id, payload);
      } else {
        await clienteService.crear(payload);
      }
      onGuardado();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card extra="w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="mb-4 text-lg font-bold text-navy-700 dark:text-white">
          {cliente ? "Editar cliente" : "Nuevo cliente"}
        </h3>

        {error && (
          <div className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={guardar} className="space-y-3">
          <Campo
            name="nombre"
            label="Nombre"
            value={form.nombre ?? ""}
            onChange={cambiar}
            required
          />
          <Campo
            name="slug"
            label="Identificador en la URL"
            value={form.slug ?? ""}
            onChange={cambiar}
            ayuda="Se genera solo desde el nombre si lo dejás vacío."
          />
          <div>
            <label
              htmlFor="tipo_evaluacion"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Tipo de evaluación
            </label>
            <select
              id="tipo_evaluacion"
              name="tipo_evaluacion"
              value={form.tipo_evaluacion ?? ""}
              onChange={cambiar}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              <option value="">Seleccioná una evaluación</option>
              {tipos.map((t) => (
                <option key={t.clave} value={t.clave}>
                  {t.nombre}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-400">
              {cliente?.campaign_id
                ? `Campaña ${cliente.campaign_id} ya creada en Reskilling.`
                : "La campaña se crea sola al guardar."}
            </p>
          </div>
          <Campo
            name="logo_url"
            label="URL del logo"
            value={form.logo_url ?? ""}
            onChange={cambiar}
          />
          <Campo
            name="logo_secundario_url"
            label="URL del segundo logo"
            value={form.logo_secundario_url ?? ""}
            onChange={cambiar}
            ayuda="Opcional, si la institución se presenta con más de una marca."
          />
          <Campo
            name="titulo"
            label="Título de la landing"
            value={form.titulo ?? ""}
            onChange={cambiar}
          />

          <div>
            <label
              htmlFor="descripcion"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Descripción de la landing
            </label>
            <textarea
              id="descripcion"
              name="descripcion"
              value={form.descripcion ?? ""}
              onChange={cambiar}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div>
            <label
              htmlFor="consentimiento"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Consentimiento informado
            </label>
            <textarea
              id="consentimiento"
              name="consentimiento"
              value={form.consentimiento ?? ""}
              onChange={cambiar}
              rows={6}
              className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none"
            />
            <p className="mt-1 text-xs text-gray-400">
              Si lo dejás vacío se usa el texto general. Admite ## para títulos
              y - para viñetas.
            </p>
          </div>

          <Campo
            name="email_notificaciones"
            label="Email para copia de los informes"
            type="email"
            value={form.email_notificaciones ?? ""}
            onChange={cambiar}
            ayuda="Opcional: recibe una copia de cada informe."
          />

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              name="activo"
              checked={form.activo ?? true}
              onChange={cambiar}
            />
            Activo
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCerrar}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={guardando}
              className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
            >
              {guardando ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Campo({
  name,
  label,
  value,
  onChange,
  type = "text",
  required,
  ayuda,
}: {
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  ayuda?: string;
}) {
  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1 block text-sm font-medium text-gray-700"
      >
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
      {ayuda && <p className="mt-1 text-xs text-gray-400">{ayuda}</p>}
    </div>
  );
}

export default Clientes;
